//

import Dexie from "dexie"
import { mapLanguageValueToKey } from "@/lang/config"
import {
	inferMediaKind,
	registerGameMediaUrlKind,
} from "@/utils/gameMedia"
import type { GameMediaKind } from "@/utils/gameMedia"

// USE THE SAME DATABASE
// content data base
const db = new Dexie("Content")

let dbReady: Promise<Dexie> | null = null

const KNOWN_IMAGE_TABLES = new Set([
	"ingredient_image",
	"item_shop_image",
	"tapas_challenge_image"
])

const pendingDownloads = new Map<string, Promise<Blob>>()
const pendingTableCreations = new Map<string, Promise<void>>()

const initDB = async () => {
	if(!dbReady)
	{
		db.version(1).stores({
			// ipadHunt
			challenge: "id, data, file1, file2, filte3",
			answers: " challenge, team_id, answer",
			file_upload: "challenge, team_id, file",

			// Content image:
			ingredient_image: "id, file, url",
			item_shop_image: "id, file, url",
			tapas_challenge_image: "id, file, url"
		})
		dbReady = db.open().then(() => db)
	}
	return dbReady
}

const normalizeUrl = (url: string) => url.split("?")[0]

const MEDIA_FILE_REGEX =
	/\.(png|jpe?g|gif|webp|svg|avif|heic|heif|mp4|mov|webm|avi|m4v|mkv|mp3|wav|ogg|m4a|aac|flac|opus)(\?.*)?$/i
const LANGUAGE_TOKEN_REGEX = /(^|[/_.-])(es|en|ca)(?=$|[/_.-])/i

const getEventLanguage = (
	instance: Record<string, unknown> & { id: string | number }
) => {
	const eventLanguageRaw =
		(instance.event_language as string | undefined) ||
		(instance.language_event as string | undefined) ||
		(instance.language as string | undefined) ||
		localStorage.getItem("language") ||
		""

	return eventLanguageRaw.toLowerCase().trim()
}

const getUrlLanguageToken = (url: string) => {
	const normalized = normalizeUrl(url).toLowerCase()
	const match = normalized.match(LANGUAGE_TOKEN_REGEX)
	return match?.[2] || null
}

const getKeyLanguageToken = (key: string) => {
	const normalizedKey = key.toLowerCase()
	const match = normalizedKey.match(
		/(?:^|[_-])(es|en|lang1|lang2|lang3)(?:$|[_-])/
	)
	return match?.[1] || null
}

const shouldDownloadMediaForLanguage = (
	key: string,
	value: string,
	instance: Record<string, unknown> & { id: string | number }
) => {
	const eventLanguage = getEventLanguage(instance)
	if (!eventLanguage) {
		return true
	}

	const mediaLanguageRaw = (instance.language as string | undefined) || ""
	const mediaLanguage = mediaLanguageRaw.toLowerCase().trim()
	if (mediaLanguage && mediaLanguage !== eventLanguage) {
		return false
	}

	const keyLanguageToken = getKeyLanguageToken(key)
	if (keyLanguageToken) {
		const eventLanguageKey = mapLanguageValueToKey(eventLanguage)?.toLowerCase()
		const validLanguageTokens = new Set([
			eventLanguage === "es" || eventLanguage === "en" ? eventLanguage : null,
			eventLanguageKey
		].filter(Boolean) as string[])
		if (!validLanguageTokens.has(keyLanguageToken)) {
			return false
		}
	}

	const urlLanguageToken = getUrlLanguageToken(value)
	if (urlLanguageToken && urlLanguageToken !== eventLanguage) {
		return false
	}

	return true
}

const fetchBlobOnce = async (url: string) => {
	if (!pendingDownloads.has(url)) {
		pendingDownloads.set(
			url,
			fetch(url)
				.then(async (res) => {
					if (!res.ok) {
						throw new Error(`Failed to fetch ${url}: ${res.status}`)
					}
					return res.blob()
				})
				.finally(() => {
					pendingDownloads.delete(url)
				})
		)
	}
	return pendingDownloads.get(url)!
}

const createCachedObjectUrl = (blob: Blob, originalUrl: string, kind?: GameMediaKind) => {
	const objectUrl = URL.createObjectURL(blob)
	registerGameMediaUrlKind(objectUrl, kind ?? inferMediaKind(originalUrl, blob.type))
	return objectUrl
}

const cacheMediaInBackground = async (
	tableName: string,
	instanceId: string | number,
	url: string
) => {
	try {
		const blob = await fetchBlobOnce(url)
		await db.table(tableName).put({
			id: instanceId,
			file: blob,
			url
		})
	} catch (error) {
		console.error("Error caching media in background:", error)
	}
}

const ensureImageTable = async (tableName: string) => {
	if (db.tables.some((table) => table.name === tableName)) {
		return
	}

	if (!pendingTableCreations.has(tableName)) {
		pendingTableCreations.set(
			tableName,
			(async () => {
				const currentSchema: Record<string, string> = {}
				for (const table of db.tables) {
					const schemaParts = [
						table.schema.primKey.src,
						...table.schema.indexes.map((index) => index.src)
					].filter(Boolean)
					currentSchema[table.name] = schemaParts.join(", ")
				}

				KNOWN_IMAGE_TABLES.add(tableName)
				db.close()
				db.version(Math.ceil(db.verno) + 1).stores({
					...currentSchema,
					[tableName]: "id, file, url"
				})
				await db.open()
			})().finally(() => {
				pendingTableCreations.delete(tableName)
			})
		)
	}

	await pendingTableCreations.get(tableName)
}

const checkImageDb = async (
	contentName: string,
	instance: Record<string, unknown> & { id: string | number }
) => {
	await initDB()
	for (const key in instance) {
		try{
			const value = instance[key]

		if (
			typeof value === "string" &&
			value.includes("digitaloceanspaces.com") &&
			!key.includes("pdf") &&
			MEDIA_FILE_REGEX.test(normalizeUrl(value))
		) {
			if (!shouldDownloadMediaForLanguage(key, value, instance)) {
				continue
			}
			// is an image
			// check if is in database

			const name_db = contentName + "_" + key

			if (!KNOWN_IMAGE_TABLES.has(name_db)) {
				await ensureImageTable(name_db)
			}
			const mediaKind = inferMediaKind(value)
			const record = await db.table(name_db).get(instance.id)

			if (record?.file && record?.url) {
				if (normalizeUrl(value) === normalizeUrl(record.url)) {
					instance[key] = createCachedObjectUrl(record.file, value, mediaKind)
					continue
				}
			}

			if (mediaKind === "video") {
				void cacheMediaInBackground(name_db, instance.id, value)
				continue
			}

			const blob = await fetchBlobOnce(value)

				await db.table(name_db).put({
					id: instance.id,
					file: blob,
					url: value
				})

				instance[key] = createCachedObjectUrl(blob, value, mediaKind)
			}
		} catch (error) {
			console.error("Error checking image:", error)
		}
	}
	
	return instance
}

export { initDB, checkImageDb }
